import React from 'react';
import {NavLink, useNavigate} from 'react-router-dom';
import UserProfile from './UserProfile';
import ProjectSwitcher from './ProjectSwitcher';
import QuotaPill from './workspace/QuotaPill';
import {UploadCloudIcon} from './Icons';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {setCurrentProject, updateProjectLastActivity} from '../store/slices/projectsSlice';
import {persistProjectMetadata} from '../store/slices/projectsThunks';
import {requestAudioUpload} from '../store/slices/audioSlice';
import {currentProjectSelector, projectDataSelector} from '../store/selectors/projects.selectors';
import {isTranscribingSelector} from '../store/selectors/transcription.selectors';
import {transcriptionQuotaSelector} from '../store/selectors/usage.selectors';
import {isAdminHintSelector} from '../store/selectors/admin.selectors';

/**
 * One row, not two.
 *
 * The desktop app's header stacked a project bar over a tab strip of five coloured buttons, which
 * is a lot of chrome for an app whose whole job now happens on one page. What is left is the three
 * things that are true everywhere: which project you are in, how much transcription time you have,
 * and the one action that starts every piece of work.
 */
const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const allProjects = useAppSelector(projectDataSelector);
  const currentProject = useAppSelector(currentProjectSelector);
  const isTranscribing = useAppSelector(isTranscribingSelector);
  const quota = useAppSelector(transcriptionQuotaSelector);
  // A hint, not authorization: the claim it reads is stamped at token issue and survives a revoke.
  // It decides whether the link is drawn; the server decides whether the page opens.
  const isAdmin = useAppSelector(isAdminHintSelector);

  const projectPath = (suffix: string) => (
    currentProject?.id ? `/projects/${currentProject.id}${suffix}` : suffix
  );

  const handleProjectSwitch = (projectId: string) => {
    const selectedProject = allProjects.find(p => p.id === projectId);

    if (!selectedProject) {
      return;
    }

    const timestamp = new Date().toISOString();
    dispatch(setCurrentProject(selectedProject));
    dispatch(updateProjectLastActivity(projectId));
    dispatch(persistProjectMetadata({projectId, lastActivity: timestamp}));
    navigate(`/projects/${projectId}/workspace`);
  };

  const handleUploadClick = () => {
    // The workspace owns the picker; this only asks it to open one. Navigating first means the
    // button works from Word Lists or Account, not just from the page it belongs to.
    navigate(projectPath('/workspace'));
    dispatch(requestAudioUpload());
  };

  return (
    <nav className="flex-shrink-0 border-b border-gray-800 bg-gray-900">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-2 px-4 sm:gap-3">
        <button
          type="button"
          onClick={() => navigate(projectPath('/workspace'))}
          className="flex shrink-0 items-center gap-2 cursor-pointer"
        >
          <img src="/censorme.svg" alt="" className="h-5 w-5" />
          {/* The wordmark is the first thing to go on a narrow phone — the project switcher right
              next to it re-establishes context, and reclaiming this width is what keeps the
              right-hand cluster (quota, upload, profile) from being pushed past the screen edge. */}
          <span className="hidden text-sm font-semibold text-white sm:inline">CensorMe</span>
        </button>

        <span className="hidden text-gray-700 sm:inline">/</span>

        <ProjectSwitcher
          projects={allProjects}
          currentProject={currentProject}
          onSwitch={handleProjectSwitch}
          onViewAll={() => navigate('/projects')}
        />

        <QuotaPill quota={quota} />

        <div className="mx-2 hidden flex-1 items-center gap-1 lg:flex">
          <HeaderLink to={projectPath('/workspace')} label="Workspace" />
          <HeaderLink to={projectPath('/wordlists')} label="Word lists" />
          <HeaderLink to={projectPath('/conversions')} label="Conversions" />
          <HeaderLink to="/projects" label="Projects" end />
          {isAdmin && <HeaderLink to="/admin" label="Admin" />}
        </div>

        <div className="ml-auto flex items-center gap-2 lg:ml-0">
          {isTranscribing && (
            <button
              type="button"
              onClick={() => navigate(projectPath('/workspace'))}
              title="A transcription is running — click to see it"
              className="inline-flex items-center gap-1.5 rounded-full border border-indigo-500/40
               bg-indigo-500/10 px-2.5 py-1 text-xs text-indigo-200 hover:bg-indigo-500/20
               transition-colors cursor-pointer"
            >
              <span className="h-2.5 w-2.5 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
              Transcribing
            </button>
          )}

          <button
            type="button"
            onClick={handleUploadClick}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1.5 text-xs
             font-medium text-white hover:bg-indigo-500 transition-colors cursor-pointer sm:px-3"
          >
            <UploadCloudIcon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Upload audio</span>
          </button>

          <UserProfile />
        </div>
      </div>

      {/* Narrow screens lose the inline nav, so it wraps onto its own row rather than disappearing. */}
      <div className="flex items-center gap-1 overflow-x-auto border-t border-gray-800/70 px-4 py-1.5 lg:hidden">
        <HeaderLink to={projectPath('/workspace')} label="Workspace" />
        <HeaderLink to={projectPath('/wordlists')} label="Word lists" />
        <HeaderLink to={projectPath('/conversions')} label="Conversions" />
        <HeaderLink to="/projects" label="Projects" end />
        {isAdmin && <HeaderLink to="/admin" label="Admin" />}
      </div>
    </nav>
  );
};

const HeaderLink: React.FC<{to: string; label: string; end?: boolean}> = ({to, label, end}) => (
  <NavLink
    to={to}
    end={end}
    className={({isActive}) => `whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium
     transition-colors ${
  isActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-100'
  }`}
  >
    {label}
  </NavLink>
);

export default Header;
